import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del } from '@/services/ApiService';
import {
    GET_SHOP_CONTACT_US,
    CREATE_SHOP_CONTACT_US,
    UPDATE_SHOP_CONTACT_US,
    DELETE_SHOP_CONTACT_US,
} from '@/services/ApiRoutes';

export interface SupportChannel {
    _id?: string;
    id?: string;
    title: string;
    description: string;
    actionText: string;
    actionLink: string;
    icon?: string;
    isPrimary?: boolean;
}

export interface FaqItem {
    _id?: string;
    id?: string;
    question: string;
    answer: string;
}

export interface ShopContactData {
    _id?: string;
    id?: string;
    badge: string;
    title: string;
    subtitle: string;
    contactInfo: {
        email: string;
        salesEmail?: string;
        phone: string;
        secondaryPhone?: string;
        address: string;
        businessHours: string;
        supportAvailability: string;
    };
    supportChannels: SupportChannel[];
    faqs: FaqItem[];
    lastUpdated?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface ShopContactState {
    data: ShopContactData | null;
    loading: boolean;
    error: string | null;
}

const initialState: ShopContactState = {
    data: null,
    loading: false,
    error: null,
};

export const fetchShopContactUs = createAsyncThunk(
    'shopContact/fetch',
    async (_, { rejectWithValue }) => {
        try {
            const response = await get(GET_SHOP_CONTACT_US);
            return response.data?.data || response.data || null;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch Shop Contact Us');
        }
    }
);

export const updateShopContactUs = createAsyncThunk(
    'shopContact/update',
    async (payload: Partial<ShopContactData>, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_SHOP_CONTACT_US, payload);
            return response.data?.data || response.data || response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to save Shop Contact Us');
        }
    }
);

export const resetShopContactUs = createAsyncThunk(
    'shopContact/reset',
    async (_, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_SHOP_CONTACT_US);
            return response.data?.data || response.data || response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to reset Shop Contact Us');
        }
    }
);

const shopContactSlice = createSlice({
    name: 'shopContact',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchShopContactUs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchShopContactUs.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchShopContactUs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateShopContactUs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateShopContactUs.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(updateShopContactUs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(resetShopContactUs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(resetShopContactUs.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(resetShopContactUs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = shopContactSlice.actions;
export default shopContactSlice.reducer;
